import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Smartphone, Tablet, Globe } from "lucide-react";
import { Button } from "../ui/button";
import {
  aiCompanionCapabilities,
  communityFeatures,
  devicePlatforms,
  fragmentedExperience,
  homeEcosystem,
  homeLearnerJourney,
  homeTestimonials,
  impactMetrics,
  learningFeatures,
  opportunityCategories,
  partnerLogos,
  partnerTypes,
  PLAY_STORE_URL,
  socialProofStats,
} from "@/data/homeContent";
import { navigateToFlutterSignup } from "@/utils/flutterAppNavigation";
import { useMarketingAsset, useMarketingAssets, useMarketingImage } from "@/lib/marketingAssets";
import heroBg from "../../assets/landing/students.jpg";
import landingLessons from "../../assets/landing/Lessons.png";
import landingDiscover from "../../assets/landing/Discover.png";
import landingMuzanga from "../../assets/landing/muzanga.png";
import landingStories from "../../assets/landing/Stories.png";
import aluLogo from "../../assets/landing/Building momentum with/ALU.png";
import yyasLogo from "../../assets/landing/Building momentum with/YYAS.jpg";
import mcfLogo from "../../assets/landing/Building momentum with/MasterCardFoundation.png";
import uwcLogo from "../../assets/landing/Building momentum with/UWC.jpg";
import yygsLogo from "../../assets/landing/Building momentum with/YYGS.jpg";
import ashokaLogo from "../../assets/landing/Building momentum with/Ashoka University.png";
import ourmoonLogo from "../../assets/landing/Building momentum with/OurMoon.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SectionLabel = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-skutopia-500 mb-4 ${className || ""}`}>
    {children}
  </p>
);

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const logoImages: Record<string, string> = {
  "African Leadership University": aluLogo,
  "Yale Young African Scholars": yyasLogo,
  "Mastercard Foundation": mcfLogo,
  "United World Colleges": uwcLogo,
  "Yale Young Global Scholars": yygsLogo,
  "Ashoka University": ashokaLogo,
  OurMoon: ourmoonLogo,
};

export const HomeHero = () => {
  const heroBgUrl = useMarketingImage("home.hero.background", heroBg);
  return (
    <section className="relative min-h-screen flex items-end bg-skutopia-950 overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroBgUrl} alt="" className="w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-skutopia-950/90 via-skutopia-950/45 to-skutopia-900/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(102,165,147,0.16),transparent_55%)]" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-32 z-30">
        <motion.div initial="hidden" animate="visible" className="max-w-3xl">
          <motion.p variants={fadeUp} custom={0} className="text-skutopia-200 text-sm font-semibold tracking-[0.25em] uppercase mb-6">
            Skutopia Academy
          </motion.p>
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-8"
          >
            Learn better. Find
            <br />
            better opportunities.
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="text-lg sm:text-xl text-white/80 leading-relaxed mb-10 max-w-xl">
            Courses, short videos, Muzanga AI tutoring, and partner-published scholarships and
            programmes for African high school students.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-medium bg-white text-skutopia-900 hover:bg-skutopia-50 transition-colors"
            >
              Get the app <ArrowRight className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={() => scrollTo("ecosystem")}
              className="inline-flex items-center justify-center rounded-full px-8 py-4 text-base font-medium text-white border border-white/30 hover:bg-white/10 transition-colors"
            >
              See how it works
            </button>
          </motion.div>
        </motion.div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50">
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </div>
    </section>
  );
};

export const HomeSocialProof = () => (
  <section className="py-16 sm:py-20 bg-white border-b border-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
        {socialProofStats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="text-center"
          >
            <p className="text-3xl sm:text-4xl font-bold text-skutopia-600 mb-1">{s.value}</p>
            <p className="text-sm text-gray-500">{s.label}</p>
          </motion.div>
        ))}
      </div>
      <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
        Opportunity publishers on Discover
      </p>
      <p className="text-center text-sm text-gray-500 mb-6 max-w-xl mx-auto">
        Organisations whose programmes students can find in the app. A listing does not imply a
        formal endorsement.
      </p>
      <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
        {partnerLogos.map((p) => (
          <div key={p.name} className="flex flex-col items-center gap-2">
            <img
              src={logoImages[p.name]}
              alt={p.name}
              className="h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
            />
            <span className="text-xs font-medium text-gray-500">{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const HomeProblem = () => (
  <section className="py-24 sm:py-32 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mb-16">
        <SectionLabel>The problem</SectionLabel>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
          High school learning and opportunity should not live in different apps.
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed">
          Students jump between videos, notes, quizzes, and scholarship links that never talk to each
          other. That costs time and momentum before exams and applications.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fragmentedExperience.map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04 }}
            className="p-5 rounded-2xl bg-white border border-gray-200 hover:border-red-200/80 hover:shadow-md transition-all group"
          >
            <p className="font-semibold text-gray-900 mb-1">{item.label}</p>
            <p className="text-sm text-gray-500 group-hover:text-red-600/80 transition-colors">{item.detail}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const HomeSolution = () => {
  const solutionVideo = useMarketingAsset("home.solution.video");

  return (
    <section id="vision" className="py-24 sm:py-32 bg-skutopia-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-white mb-4">
              The solution
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-6">
              Skutopia is the high school learning and opportunity app.
            </h2>
            <p className="text-lg text-white/75 leading-relaxed mb-6">
              Study with courses and videos, get help from Muzanga, then find scholarships and programmes
              on Discover, published by universities, foundations, and programme partners.
            </p>
            <p className="text-white/60">Five tabs. One place. Built for secondary learners.</p>
          </motion.div>

          {solutionVideo?.url ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative max-w-md mx-auto w-full rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10"
            >
              <video
                src={solutionVideo.url}
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full h-auto"
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative aspect-square max-w-md mx-auto"
            >
              <div className="absolute inset-0 rounded-full border border-skutopia-400/20" />
              <div className="absolute inset-[12%] rounded-full border border-skutopia-400/30" />
              <div className="absolute inset-[24%] rounded-full border border-skutopia-400/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-28 h-28 rounded-full bg-skutopia-500 flex items-center justify-center shadow-2xl ring-4 ring-skutopia-400/30 z-10">
                  <span className="text-lg font-bold text-center leading-tight">
                    Skuto
                    <br />
                    pia
                  </span>
                </div>
              </div>
              {["Videos", "Courses", "Muzanga", "Discover", "Profile"].map((label, i) => {
                const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
                const r = 42;
                const x = 50 + r * Math.cos(angle);
                const y = 50 + r * Math.sin(angle);
                return (
                  <div
                    key={label}
                    className="absolute px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold border border-white/20"
                    style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
                  >
                    {label}
                  </div>
                );
              })}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export const HomeEcosystem = () => {
  const [hovered, setHovered] = useState<number | null>(null);
  return (
    <section id="ecosystem" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <SectionLabel>How the app works</SectionLabel>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
            Five tabs. Everything you need for school and what comes next.
          </h2>
          <p className="text-lg text-gray-600">
            The same structure students see when they open the Skutopia app.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {homeEcosystem.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.02 }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`p-5 rounded-2xl border transition-all duration-300 cursor-default ${
                hovered === i
                  ? "bg-skutopia-500 text-white border-skutopia-500 shadow-xl scale-[1.03]"
                  : "bg-gray-50 border-gray-100 hover:border-skutopia-200"
              }`}
            >
              <item.icon className={`w-6 h-6 mb-3 ${hovered === i ? "text-white" : "text-skutopia-500"}`} />
              <h3 className="font-semibold text-sm mb-2">{item.title}</h3>
              <p className={`text-xs leading-relaxed ${hovered === i ? "text-white/90" : "text-gray-500"}`}>
                {item.impact}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const HomeJourney = () => (
  <section className="py-24 sm:py-32 bg-gradient-to-b from-skutopia-50/80 to-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-20">
        <SectionLabel>Your path</SectionLabel>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
          Built for secondary school and the steps right after.
        </h2>
        <p className="text-lg text-gray-600">
          From junior secondary through exams, scholarships, and applications.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
        {homeLearnerJourney.map((stage, i) => (
          <motion.div
            key={stage}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04 }}
            className="flex items-center gap-3"
          >
            <span className="px-4 py-2.5 rounded-full bg-white border border-skutopia-200 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md hover:border-skutopia-400 transition-all">
              {stage}
            </span>
            {i < homeLearnerJourney.length - 1 && <span className="text-skutopia-300 hidden sm:inline">→</span>}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CATEGORY_ASSET_KEYS: Record<string, string> = {
  "High school programs": "home.discover.category.high_school_programs",
  Scholarships: "home.discover.category.scholarships",
  "Summer schools": "home.discover.category.summer_schools",
  "Exchange & global": "home.discover.category.exchange_global",
  "Capacity building": "home.discover.category.capacity_building",
  "University pathways": "home.discover.category.university_pathways",
};

export const HomeOpportunities = () => {
  const discoverPhoneUrl = useMarketingImage("home.discover.phone", landingDiscover);
  const overrides = useMarketingAssets();

  return (
    <section className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <SectionLabel>Discover</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4">
            Opportunities partners publish for high school students.
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Browse examples here, then apply and track in the app.
          </p>
          <Link
            to="/programs"
            className="inline-flex items-center text-skutopia-600 font-semibold hover:text-skutopia-700"
          >
            See Discover examples <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {opportunityCategories.map((cat, i) => {
            const assetKey = CATEGORY_ASSET_KEYS[cat.title];
            const image = (assetKey && overrides[assetKey]?.url) || cat.image;
            return (
              <Link key={cat.title} to={cat.link}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group h-full rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={image}
                      alt={cat.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <cat.icon className="w-5 h-5 text-skutopia-500 mb-2" />
                    <h3 className="font-bold text-gray-900 mb-2">{cat.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{cat.description}</p>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </div>
        <div className="mt-12 flex justify-center">
          <img src={discoverPhoneUrl} alt="Discover in the Skutopia app" className="w-full max-w-sm" />
        </div>
      </div>
    </section>
  );
};

export const HomeLearning = () => {
  const learningPhoneUrl = useMarketingImage("home.learning.phone", landingLessons);
  return (
    <section className="py-24 sm:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionLabel>Videos & Courses</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Study with structure, not random tabs.
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Short videos, curriculum-aligned courses, quizzes, and flashcards in one rhythm you can
              return to every day.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {learningFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-skutopia-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <img src={learningPhoneUrl} alt="Courses in Skutopia" className="w-full max-w-sm mx-auto" />
        </div>
      </div>
    </section>
  );
};

export const HomeCommunity = () => {
  const communityPhoneUrl = useMarketingImage("home.community.phone", landingStories);
  return (
    <section className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <img
            src={communityPhoneUrl}
            alt="Student stories"
            className="w-full max-w-sm mx-auto order-2 lg:order-1"
          />
          <div className="order-1 lg:order-2">
            <SectionLabel>Community</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Study with Skutopians.
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Study groups, chat, and student stories live alongside your learning feed. Skutopians are students who are using the Skutopia app.
            </p>
            <div className="space-y-4">
              {communityFeatures.map((f, i) => (
                <motion.div
                  key={f}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.3 }}
                  className="p-4 rounded-xl bg-skutopia-50/50 border border-skutopia-100 text-gray-700 text-sm"
                >
                  {f}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const HomeAI = () => {
  const muzangaPhoneUrl = useMarketingImage("home.muzanga.phone", landingMuzanga);
  return (
    <section className="py-24 sm:py-32 bg-gradient-to-br from-skutopia-900 via-skutopia-800 to-skutopia-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-white mb-4">
              Muzanga AI
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6">
              Help that understands your syllabus.
            </h2>
            <p className="text-lg text-white/75 mb-8">
              Ask questions, work through concepts, and revise with guidance grounded in curriculum
              context.
            </p>
            <ul className="space-y-3">
              {aiCompanionCapabilities.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-white/85">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-skutopia-300 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
          <img src={muzangaPhoneUrl} alt="Muzanga AI" className="w-full max-w-sm mx-auto" />
        </div>
      </div>
    </section>
  );
};

export const HomeImpact = () => (
  <section className="py-24 sm:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <SectionLabel>Growing with learners</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
          Momentum across classrooms and Discover.
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {impactMetrics.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100"
          >
            <p className="text-2xl sm:text-3xl font-bold text-skutopia-600 mb-1">{m.value}</p>
            <p className="text-xs sm:text-sm text-gray-500">{m.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const HomePartners = () => (
  <section className="py-24 sm:py-32 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <SectionLabel>For opportunity publishers</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Reach high school students where they already study.
        </h2>
        <p className="text-lg text-gray-600">
          Publish scholarships, summer programmes, and placements to Discover using the same portal
          partners use in the Skutopia CRM.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {partnerTypes.map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="p-6 rounded-2xl bg-white border border-gray-100 hover:border-skutopia-200 hover:shadow-lg transition-all"
          >
            <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{p.body}</p>
          </motion.div>
        ))}
      </div>
      <div className="text-center">
        <Link to="/partners/login">
          <Button
            variant="outline"
            className="rounded-full px-8 py-6 h-auto border-skutopia-500 text-skutopia-600 hover:bg-skutopia-50 font-semibold"
          >
            Partner with Skutopia <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

export const HomeTestimonials = () => (
  <section className="py-24 sm:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <SectionLabel>Stories</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">From classrooms and Discover.</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {homeTestimonials.map((t, i) => (
          <motion.blockquote
            key={t.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="p-8 rounded-3xl bg-gray-50 border border-gray-100 flex flex-col"
          >
            <span className="self-start px-3 py-1 rounded-full bg-skutopia-100 text-skutopia-700 text-xs font-semibold mb-6">
              {t.theme}
            </span>
            <p className="text-gray-700 leading-relaxed flex-1 mb-6 italic">&ldquo;{t.story}&rdquo;</p>
            <footer>
              <cite className="not-italic font-bold text-gray-900">{t.name}</cite>
              <p className="text-sm text-gray-500">{t.role}</p>
            </footer>
          </motion.blockquote>
        ))}
      </div>
    </div>
  </section>
);

const deviceIcons = [Smartphone, Tablet];

export const HomeDownload = () => (
  <section className="py-24 sm:py-32 bg-skutopia-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <SectionLabel>Get started</SectionLabel>
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Your study day, on your phone.</h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
        Free on Google Play. iOS coming soon.
      </p>
      <div className="grid sm:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
        {devicePlatforms.map((d, i) => {
          const Icon = deviceIcons[i] ?? Globe;
          return (
            <div key={d.name} className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm">
              <Icon className="w-8 h-8 text-skutopia-500 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-1">{d.name}</h3>
              <p className="text-sm text-gray-500">{d.description}</p>
            </div>
          );
        })}
      </div>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 px-8 py-4 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 transition-colors"
      >
        <Smartphone className="w-6 h-6" />
        <div className="text-left">
          <div className="text-xs opacity-80">GET IT ON</div>
          <div className="text-lg font-semibold">Google Play</div>
        </div>
      </a>
    </div>
  </section>
);

export const HomeFinalCTA = () => (
  <section className="py-28 sm:py-36 bg-gradient-to-br from-skutopia-600 via-skutopia-500 to-skutopia-700 text-white relative">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_50%)]" />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
        <SectionLabel className="text-white">Join</SectionLabel>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-8">
          Start learning. Start discovering.
        </h2>
        <p className="text-xl text-white/80 leading-relaxed mb-12 max-w-2xl mx-auto">
          Students: get the app. Opportunity publishers: list programmes on Discover.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={navigateToFlutterSignup}
            className="bg-white text-skutopia-600 hover:bg-skutopia-50 rounded-full px-10 py-6 h-auto font-semibold text-base"
          >
            Create a student account <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <Link to="/partners/login">
            <Button
              variant="outline"
              className="rounded-full px-10 py-6 h-auto font-semibold text-base border-white/40 text-white hover:bg-white/10 bg-transparent w-full sm:w-auto"
            >
              Partner with Skutopia
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);
